/**
 * La relation de voisinage entre le visiteur et la personne consultée.
 *
 * POURQUOI CE SERVICE : les règles de confidentialité savent depuis v7.10
 * qu'un voisin voit plus qu'un inconnu — l'adresse exacte, et les
 * coordonnées que la personne a ouvertes à sa rue. Mais rien ne leur
 * transmettait la relation : la règle était écrite et inerte.
 *
 * ELLE N'ÉCHOUE JAMAIS. Un appel qui rate rend « pas voisins », et la fiche
 * s'affiche alors comme pour un inconnu. C'est le bon sens de l'erreur :
 * une panne ne doit pas ouvrir l'accès à une adresse.
 *
 * MAIS CE REPLI MASQUE AUSSI LES PANNES, et il l'a fait. La route exige une
 * identité — elle dit qui partage un quartier avec qui. Sans jeton elle
 * répond 401, le repli rend « inconnu », et TOUTE fiche s'affiche comme
 * celle d'un étranger : la fonctionnalité entière devient inerte sans qu'un
 * seul message ne le signale. Le jeton est donc ce qui distingue « ils ne
 * sont pas voisins » de « on n'a pas pu savoir ».
 */

import { enTetesAuth } from './enTetesAuth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://apicabaza.creago.be/rest/cabaza';

/** Ce qu'on rend quand on ne sait pas. Rien n'est ouvert. */
const INCONNU = {
    partageUnQuartier: false,
    reglagesQuartier: null,
    quartiers: []
};

/**
 * Suis-je voisin de cette personne, et qu'a-t-elle ouvert à ses voisins ?
 *
 * @param {string} peopleUuid la personne consultée
 * @returns {Promise<{partageUnQuartier: boolean, reglagesQuartier: object|null, quartiers: Array}>}
 */
export async function lireVoisinage(peopleUuid) {
    if (!peopleUuid) return INCONNU;

    try {
        const reponse = await fetch(
            `${API_BASE_URL}/neighborhood/voisins-avec/${encodeURIComponent(peopleUuid)}`,
            { method: 'GET', headers: enTetesAuth(false), credentials: 'include' }
        );

        if (!reponse.ok) return INCONNU;

        const data = await reponse.json();

        // ColdFusion rend les clés en majuscules sur cette route.
        const partage = data.partageUnQuartier ?? data.PARTAGEUNQUARTIER ?? false;
        if (!partage) return INCONNU;

        const reglages = data.reglagesQuartier ?? data.REGLAGESQUARTIER ?? null;

        return {
            partageUnQuartier: true,
            reglagesQuartier: normaliserReglages(reglages),
            quartiers: data.quartiers ?? data.QUARTIERS ?? []
        };
    } catch (erreur) {
        // Une panne ne doit pas ouvrir une adresse : on répond « inconnu ».
        console.warn('Relation de voisinage indisponible:', erreur);
        return INCONNU;
    }
}

/**
 * Met les réglages dans la forme qu'attend confidentialiteProfil.js.
 *
 * Les clés arrivent dans une casse imprévisible ; une clé mal lue vaudrait
 * « fermé », ce qui est le bon défaut mais masquerait un partage voulu.
 */
function normaliserReglages(brut) {
    if (!brut || typeof brut !== 'object') return null;

    const lire = (cle) => {
        const v = brut[cle] ?? brut[cle.toLowerCase()] ?? brut[cle.toUpperCase()];
        return v === true || v === 1 || v === '1';
    };

    return {
        showPhone: lire('showPhone'),
        showGsm: lire('showGsm'),
        showEmail: lire('showEmail')
    };
}
